/**
 * Redux store configuration.
 *
 * This file defines the application's single Redux store using Redux Toolkit and sets up persistence
 * for selected reducer state via `redux-persist`.
 *
 * Data flow (high level):
 * - UI components dispatch actions/thunks (often from `src/store/actions/*`).
 * - Slice reducers update state in response to actions.
 * - Some state (e.g., teacher earnings) is persisted to localStorage and rehydrated on app load.
 */

// src/store/store.js
import { configureStore } from "@reduxjs/toolkit";
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import studentsSlice from './slices/studentsSlice'
import teacherSlice from './slices/teacherSlice'
import coursesSlice from "./slices/coursesSlice";
import enquirySlice from "./slices/enquirySlice";
import packagesSlice from "./slices/packagesSlice";
import paymentSlice from "./slices/paymentSlice";
// Notification slices removed
import walletSlice from "./slices/walletSlice"; 
import meetingReducer from './slices/meetingSlice';
import evaluationSlice from './slices/evaluationSlice';
import trialBookingReducer from './slices/trialPreferencesSlice';
import { teacherSessionReducer, studentSessionReducer } from './slices/teacherSessionSlice';
import teacherEarningsReducer from './slices/teacherWalletSlice';
import teacherNotesReducer from './slices/teacherNotesSlice';
import slotSlice from "./slices/slotSlice"; // Import the new slot slice
import bookingsSlice from "./slices/bookingSlice"; // Keep the old booking slice
import dashboardSlice from "./slices/dashboardSlice"; // Import the new dashboard slice
import exportSlice from "./slices/exportSlice"; // Import the new export slice
// Live notification slices removed
import onlineUsersSlice from "./slices/onlineUsersSlice"; // Import online users slice
import novuSlice from "./slices/novuSlice"; // Import Novu slice for enhanced notifications
import guideSlice from "../components/guided-tour/store/guideSlice"; // Import interactive guide slice
import jitsiSlice from "./slices/jitsiSlice"; // Import Jitsi JWT slice
import ghostLoginSlice from "./slices/ghostLoginSlice"; // Import Ghost Login slice
import counsellorSlice from "./slices/counsellorSlice"; // Import Counsellor slice
import counsellorNotesSlice from "./slices/counsellorNotesSlice"; // Import Counsellor Notes slice
import activityLogSlice from "./slices/activityLogSlice";
import voiceLeadsSlice from "./slices/voiceLeadsSlice";

/**
 * Persist configuration for teacher earnings.
 *
 * We keep the persisted surface area small to avoid storing stale or non-essential data and to reduce
 * the risk of serializable warnings during rehydration.
 */
const walletPersistConfig = {
  key: 'teacherEarnings',
  storage,
  whitelist: ['balance', 'transactions', 'stats', 'lastFetched'] // Only persist these fields
};

// Wrap the earnings reducer so it can be rehydrated from localStorage by redux-persist.
const persistedWalletReducer = persistReducer(walletPersistConfig, teacherEarningsReducer);

/**
 * Configure the store and middleware.
 *
 * The `serializableCheck` ignores specific action types and state paths where the app knowingly stores
 * non-serializable values (e.g., API payloads containing Dates or large nested structures).
 */
export const store = configureStore({
  reducer: {
    students: studentsSlice,
    teachers: teacherSlice,
    courses: coursesSlice,
    enquirys: enquirySlice,
    packages: packagesSlice,
    payments: paymentSlice,
    slots: slotSlice,
    bookings: bookingsSlice,
    dashboard: dashboardSlice,
    exports: exportSlice,
    trialBooking: trialBookingReducer,
    wallet: walletSlice,
    meeting: meetingReducer,
    teacherSession: teacherSessionReducer,
    studentSession: studentSessionReducer,
    evaluations: evaluationSlice,
    teacherEarnings: persistedWalletReducer,
    teacherNotes: teacherNotesReducer,
    onlineUsers: onlineUsersSlice,
    novu: novuSlice,
    guide: guideSlice,
    jitsi: jitsiSlice,
    ghostLogin: ghostLoginSlice,
    counsellor: counsellorSlice,
    counsellorNotes: counsellorNotesSlice,
    activityLog: activityLogSlice,
    voiceLeads: voiceLeadsSlice
  },
  /**
   * Executes logic in `middleware`.
   * @param {any} getDefaultMiddleware Input value.
   * @returns {any} Returns a value of type `any`.
   */
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [
          // Existing ignored actions
          'bookings/getbookings',
          'bookings/getbookings/fulfilled',
          'bookings/Add_booking/fulfilled',
          'bookings/createBookingsWithWallet/fulfilled',
          'bookings/Updatebooking/fulfilled',
          'bookings/GetBookingsByStudentID/fulfilled',
          'bookings/GetBookingsByTeacherID/fulfilled',
          'evaluations/getStudentEvaluations/fulfilled',
          'evaluations/getEvaluationStats/fulfilled',
          'teacherSession/joinSession/fulfilled',
          'teacherSession/getStatus/fulfilled',
          'studentSession/joinSession/fulfilled',
          'studentSession/getStatus/fulfilled',
          
          // Teacher earnings ignored actions
          'teacherEarnings/setRate/fulfilled',
          'teacherEarnings/creditLesson/fulfilled',
          'teacherEarnings/getBalance/fulfilled',
          'teacherEarnings/getHistory/fulfilled',
          
          // Teacher notes ignored actions
          'teacherNotes/createNote/fulfilled',
          'teacherNotes/getStudentNotes/fulfilled',
          'teacherNotes/getTeacherNotes/fulfilled',
          'teacherNotes/getNoteById/fulfilled',
          'teacherNotes/updateNote/fulfilled',
          'teacherNotes/toggleArchiveStatus/fulfilled',
          'teacherNotes/getNotesByBooking/fulfilled',
          'teacherNotes/getRecentNotes/fulfilled',
          
          // New slot management ignored actions
          'slots/createTeacherAvailability/fulfilled',
          'slots/getTeacherAvailability/fulfilled',
          'slots/bookSlot/fulfilled',
          'slots/cancelBooking/fulfilled',
          'slots/rescheduleBooking/fulfilled',
          'slots/completeBooking/fulfilled',
          'slots/updateAttendance/fulfilled',
          'slots/getUpcomingBookings/fulfilled',
          'slots/getBookingHistory/fulfilled',
          'slots/getBookingDetails/fulfilled',
          'slots/searchBookings/fulfilled',
          'slots/migrateOldBookings/fulfilled',
          'slots/teacherJoinSession/fulfilled',
          'slots/studentJoinSession/fulfilled',
          
          // Dashboard ignored actions
          'dashboard/getAdminDashboardStats/fulfilled',
          'dashboard/getAdminDashboardStats/pending',
          'dashboard/getAdminDashboardStats/rejected',
          
          // Export ignored actions
          'exports/exportStudentsToExcel/fulfilled',
          'exports/exportStudentsToExcel/pending',
          'exports/exportStudentsToExcel/rejected',
          'exports/exportTeachersToExcel/fulfilled',
          'exports/exportTeachersToExcel/pending',
          'exports/exportTeachersToExcel/rejected',
          'exports/exportBookingsToExcel/fulfilled',
          'exports/exportBookingsToExcel/pending',
          'exports/exportBookingsToExcel/rejected',
          'exports/exportPackagesToExcel/fulfilled',
          'exports/exportPackagesToExcel/pending',
          'exports/exportPackagesToExcel/rejected',
          'exports/exportPaymentsToExcel/fulfilled',
          'exports/exportPaymentsToExcel/pending',
          'exports/exportPaymentsToExcel/rejected',
          'exports/exportCoursesToExcel/fulfilled',
          'exports/exportCoursesToExcel/pending',
          'exports/exportCoursesToExcel/rejected',
          'exports/exportEnquiriesToExcel/fulfilled',
          'exports/exportEnquiriesToExcel/pending',
          'exports/exportEnquiriesToExcel/rejected',
          'exports/exportAllDataToExcel/fulfilled',
          'exports/exportAllDataToExcel/pending',
          'exports/exportAllDataToExcel/rejected',
          // Redux persist actions
          'persist/PERSIST',
          'persist/REHYDRATE'
        ],
        ignoredPaths: [
          // Existing ignored paths
          'bookings.Allbookinglist',
          'bookings.Teacher_Bookings',
          'bookings.StudentID_Booking',
          'bookings.existingBookings',
          'evaluations.evaluations',
          'evaluations.stats',
          'teacherSession.sessionDetails',
          'studentSession.sessionDetails',
          
          // Teacher earnings ignored paths
          'teacherEarnings.earningsHistory',
          'teacherEarnings.stats',
          'teacherEarnings.transactions',
          
          // Teacher notes ignored paths
          'teacherNotes.allNotes',
          'teacherNotes.studentNotes',
          'teacherNotes.bookingNotes',
          'teacherNotes.currentNote',
          'teacherNotes.recentNotes',
          'teacherNotes.statistics',
          
          // Slots management ignored paths
          'slots.teacherAvailability',
          'slots.upcomingBookings',
          'slots.bookingHistory',
          'slots.currentBooking',
          'slots.searchResults',
          'slots.migrationStats',
          'slots.sessionDetails',
          'slots.teacherSessionActive',
          'slots.studentSessionActive',
          
          // Dashboard ignored paths
          'dashboard.overview',
          'dashboard.growth',
          'dashboard.bookings',
          'dashboard.financial',
          'dashboard.teachers',
          'dashboard.packages',
          'dashboard.courses',
          'dashboard.enquiries',
          // 'dashboard.notifications' - removed,
          'dashboard.activity',
          'dashboard.lastUpdated',
          
          // Export ignored paths
          'exports.exportHistory',
          'exports.lastExport',
          'exports.exportStats',
          'exports.error',
          // Redux persist paths
          'register',
          '_persist'
        ],
        warnAfter: 800
      },
      immutableCheck: { warnAfter: 800 }
    }).concat()
});

export const persistor = persistStore(store);
export default store;
